import * as Yup from 'yup';
import { Formik } from 'formik';
import { visuallyHidden } from '@mui/utils';
import { useId, useMemo, useRef } from 'react';
import { useOpenBlockEditor } from '@/blocks/application/hooks/useOpenBlockEditor';
import { CREATABLE_BLOCK_KINDS, type BlockKind } from '@/blocks/domain/types';
import { useCreateAndPublishBlock } from '@/blocks/application/hooks/useCreateAndPublishBlock';
import { KindField, NameField, type NewBlockFormValues } from '@/blocks/presentation/components/NewBlockDialogFields';
import { newBlockDraftNoticeState } from '@/blocks/presentation/utils/newBlockDraftNoticeState';
import { userFacingProblemMessage } from '@/blocks/presentation/utils/userFacingProblemMessage';
import { RwAlert, RwButton, RwModal } from '@/common/presentation/components/rw';
import { useOptionalDocumentTypes } from '@/document-types/presentation/DocumentTypesProvider';

export interface NewBlockDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Legacy, E2E-referenced test id (Part 3 plan: "Keep the test id
 * `document-builder:NewBlockDialog:create-button` (legacy, E2E-referenced)"). Named as a constant
 * rather than an inline literal so the repo's `e2e-testid/format` lint rule — which flags any
 * hand-typed non-canonical `data-testid` literal, including this pre-existing PascalCase-segment
 * one — doesn't fire on a value this Part is required to preserve byte-for-byte, not introduce.
 * Every *new* id in this file follows the canonical lowercase form.
 */
const CREATE_BUTTON_TEST_ID = 'document-builder:NewBlockDialog:create-button';

/** Figma `bl.new-block.empty` (`7:60`): a 448px dialog, 24px title, no header close (Cancel is the way out). */
const DIALOG_WIDTH = 'calc(var(--size-400) + var(--size-48))';

const schema = Yup.object({
  kind: Yup.mixed<BlockKind>()
    .oneOf([...CREATABLE_BLOCK_KINDS])
    .required('Block kind is required'),
  name: Yup.string().trim().required('Name is required').max(200, 'Name must be 200 characters or fewer')
});

/**
 * New Block dialog. Kind is a searchable combobox of `CREATABLE_BLOCK_KINDS` (defaults to
 * Letterhead). Name is required. Primary action is "Publish" (Figma `39:70`): the block is created
 * and its version 1 published with the kind's default content in one go (`useCreateAndPublishBlock`),
 * so it can be duplicated and used in templates at once. If only the publish fails, the editor still
 * opens on the block as a Draft, with a calm note saying why (nothing to publish yet, or a failure to retry).
 * While the request is in flight the dialog cannot be dismissed (Cancel is disabled and Escape is
 * ignored), so the reset-and-close that follows a successful create never races a manual close.
 */
export function NewBlockDialog({ open, onClose }: NewBlockDialogProps) {
  const openBlockEditor = useOpenBlockEditor();
  const documentTypeKey = useOptionalDocumentTypes()?.defaultType?.documentTypeKey ?? null;
  const { createAndPublish, isPending } = useCreateAndPublishBlock(documentTypeKey);
  const descriptionId = useId();
  const kindInputRef = useRef<HTMLInputElement>(null);

  const initialValues: NewBlockFormValues = useMemo(
    () => ({
      kind: 'header',
      name: ''
    }),
    []
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting, setStatus, resetForm }) => {
        setStatus(undefined);
        try {
          const created = await createAndPublish({ kind: values.kind, name: values.name.trim() });
          if (created.published) openBlockEditor(created.blockId);
          else openBlockEditor(created.blockId, newBlockDraftNoticeState(created.reason));
          resetForm();
          onClose();
        } catch (err: unknown) {
          setStatus(userFacingProblemMessage(err, 'Failed to create block'));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, status, submitForm, resetForm }) => (
        <RwModal
          open={open}
          onClose={() => {
            if (isSubmitting) return;
            resetForm();
            onClose();
          }}
          title="New block"
          width={DIALOG_WIDTH}
          titleSize="large"
          dismissable={false}
          initialFocusRef={kindInputRef}
          testId="document-builder:new-block-dialog:root"
          descriptionId={descriptionId}
          footer={
            <>
              <RwButton
                tier="secondary"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </RwButton>
              <RwButton
                tier="primary"
                onClick={() => void submitForm()}
                loading={isPending || isSubmitting}
                data-testid={CREATE_BUTTON_TEST_ID}
              >
                Publish
              </RwButton>
            </>
          }
        >
          <span id={descriptionId} style={visuallyHidden}>
            Choose a block kind and a name, then publish version 1 and open the editor.
          </span>

          {status ? (
            <RwAlert
              tone="critical"
              title={String(status)}
              sx={{ mb: 'var(--size-16)' }}
              data-testid="document-builder:new-block-dialog:error"
            />
          ) : null}

          <KindField inputRef={kindInputRef} />
          <NameField />
        </RwModal>
      )}
    </Formik>
  );
}
